import {
  Alert,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type {
  CustomViewWidget,
  CustomViewWidgetDefinition,
  CustomViewWidgetDisplay,
  CustomViewWidgetSeries,
  CustomViewWidgetType,
} from '../../core/types/data-models';
import { clampSpan, defaultRequestFor, GRID_COLUMNS, WIDGET_TYPES } from './widget-utils';

const SPAN_OPTIONS = Array.from({ length: GRID_COLUMNS }, (_, i) => i + 1);

interface WidgetDialogProps {
  open: boolean;
  onClose: () => void;
  customViewId: number;
  /** Widget being edited; `null` creates a new one. */
  widget: CustomViewWidget | null;
  /** `order` assigned to a newly created widget (append at the end). */
  nextOrder: number;
}

/** Everything the dialog edits, in the widget's persisted shape. */
interface WidgetDraft {
  title: string;
  description: string;
  dataSource: string;
  size: number;
  rows: number;
  type: CustomViewWidgetType;
  prompt: string;
  stylePrompt: string;
  markdown: string;
  request: Record<string, unknown>;
  /** Carried through edits so a skipped regeneration doesn't drop them. */
  series?: CustomViewWidgetSeries[];
  display?: CustomViewWidgetDisplay;
}

const EMPTY_DRAFT: WidgetDraft = {
  title: '',
  description: '',
  dataSource: 'report-instance-brand-vw',
  size: 1,
  rows: 1,
  type: 'bar',
  prompt: '',
  stylePrompt: '',
  markdown: '',
  request: defaultRequestFor('report-instance-brand-vw'),
};

function draftFromWidget(widget: CustomViewWidget): WidgetDraft {
  const def = widget.contentDefinition;
  return {
    title: widget.title,
    description: widget.description ?? '',
    dataSource: widget.dataSource,
    size: clampSpan(widget.size),
    rows: clampSpan(widget.rows),
    type: def.type ?? 'bar',
    prompt: def.prompt ?? '',
    stylePrompt: def.stylePrompt ?? '',
    markdown: def.markdown ?? '',
    request: def.request ?? defaultRequestFor(widget.dataSource),
    series: def.series,
    display: def.display,
  };
}

/** The widget JSON shown/edited in the technical view. */
function draftToJson(draft: WidgetDraft): string {
  return JSON.stringify(
    {
      title: draft.title,
      description: draft.description || null,
      dataSource: draft.dataSource,
      size: draft.size,
      rows: draft.rows,
      contentDefinition: buildDefinition(draft),
    },
    null,
    2,
  );
}

function buildDefinition(draft: WidgetDraft): CustomViewWidgetDefinition {
  const def: CustomViewWidgetDefinition = { type: draft.type };
  if (draft.prompt.trim()) def.prompt = draft.prompt.trim();
  if (draft.type === 'markdown') {
    def.markdown = draft.markdown;
  } else {
    if (draft.stylePrompt.trim()) def.stylePrompt = draft.stylePrompt.trim();
    def.request = draft.request;
    if (draft.series?.length) def.series = draft.series;
    if (draft.display) def.display = draft.display;
  }
  return def;
}

/** Parse the technical JSON back into a draft. Throws on invalid shape. */
function draftFromJson(text: string, base: WidgetDraft): WidgetDraft {
  const parsed = JSON.parse(text) as Record<string, unknown>;
  const def = (parsed.contentDefinition ?? {}) as Partial<CustomViewWidgetDefinition>;
  const type = def.type ?? base.type;
  if (!WIDGET_TYPES.includes(type)) {
    throw new Error(`contentDefinition.type must be one of: ${WIDGET_TYPES.join(', ')}`);
  }
  return {
    title: typeof parsed.title === 'string' ? parsed.title : '',
    description: typeof parsed.description === 'string' ? parsed.description : '',
    dataSource: typeof parsed.dataSource === 'string' ? parsed.dataSource : base.dataSource,
    size: clampSpan(Number(parsed.size ?? base.size)),
    rows: clampSpan(Number(parsed.rows ?? base.rows)),
    type,
    prompt: typeof def.prompt === 'string' ? def.prompt : '',
    stylePrompt: typeof def.stylePrompt === 'string' ? def.stylePrompt : '',
    markdown: typeof def.markdown === 'string' ? def.markdown : '',
    request: (def.request as Record<string, unknown> | undefined) ?? base.request,
    series: Array.isArray(def.series) ? (def.series as CustomViewWidgetSeries[]) : undefined,
    display:
      def.display && typeof def.display === 'object' && !Array.isArray(def.display)
        ? (def.display as CustomViewWidgetDisplay)
        : undefined,
  };
}

/**
 * Add/edit dialog for one custom-view widget. The friendly form covers
 * title / description / data source / display type / size plus the
 * free-text definition of what the widget should show; the "Technical
 * definition" switch swaps the form for the widget's raw JSON (including
 * the flat-view analytics request), kept in sync in both directions.
 */
export function WidgetDialog({ open, onClose, customViewId, widget, nextOrder }: WidgetDialogProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<WidgetDraft>(EMPTY_DRAFT);
  const [techMode, setTechMode] = useState(false);
  const [techText, setTechText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Re-seed the form whenever the dialog opens (create vs edit).
  useEffect(() => {
    if (!open) return;
    setDraft(widget ? draftFromWidget(widget) : EMPTY_DRAFT);
    setTechMode(false);
    setTechText('');
    setError(null);
  }, [open, widget]);

  const dataSources = useQuery({
    queryKey: ['custom-view-data-sources'],
    queryFn: () => resources.customViewDataSources(),
  });

  const save = useMutation({
    mutationFn: (payload: Partial<CustomViewWidget>) => {
      const service = resources.customViewWidgetsFor(customViewId);
      return widget
        ? service.update(widget.id, payload)
        : service.create({ ...payload, order: nextOrder });
    },
    onSuccess: () => {
      // The saved row carries `contentDefinition.generationJobId` when the
      // backend kicked a request regeneration — WidgetCard polls that job
      // and refreshes itself when it completes.
      queryClient.invalidateQueries({ queryKey: ['custom-view-widgets', customViewId] });
      onClose();
    },
  });

  const set = <K extends keyof WidgetDraft>(key: K, value: WidgetDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const handleTechToggle = (next: boolean) => {
    setError(null);
    if (next) {
      setTechText(draftToJson(draft));
      setTechMode(true);
      return;
    }
    // Leaving tech mode applies the JSON back onto the form.
    try {
      setDraft(draftFromJson(techText, draft));
      setTechMode(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    let effective = draft;
    if (techMode) {
      try {
        effective = draftFromJson(techText, draft);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Invalid JSON');
        return;
      }
    }
    if (!effective.title.trim()) {
      setError('Title is required');
      return;
    }
    try {
      await save.mutateAsync({
        title: effective.title.trim(),
        description: effective.description.trim() || null,
        dataSource: effective.dataSource,
        size: clampSpan(effective.size),
        rows: clampSpan(effective.rows),
        contentDefinition: buildDefinition(effective),
      });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <FormDialog
      open={open}
      title={widget ? 'Edit widget' : 'New widget'}
      submitLabel={widget ? 'Save' : 'Create'}
      loading={save.isPending}
      maxWidth="md"
      onClose={onClose}
      onSubmit={handleSubmit}
      titleActions={
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={techMode}
              onChange={(e) => handleTechToggle(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Technical definition</Typography>}
        />
      }
    >
      {error && <Alert severity="error">{error}</Alert>}

      {techMode ? (
        <TextField
          label="Widget JSON definition"
          fullWidth
          multiline
          minRows={16}
          value={techText}
          onChange={(e) => setTechText(e.target.value)}
          slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: 13 } } }}
          helperText="Full widget configuration, including the flat-view analytics request object."
        />
      ) : (
        <Stack spacing={2}>
          <TextField
            label="Title"
            required
            autoFocus
            fullWidth
            value={draft.title}
            onChange={(e) => set('title', e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={draft.description}
            onChange={(e) => set('description', e.target.value)}
          />
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="widget-data-source-label">Data source</InputLabel>
              <Select
                labelId="widget-data-source-label"
                label="Data source"
                value={draft.dataSource}
                onChange={(e) => {
                  const next = e.target.value;
                  setDraft((d) => ({
                    ...d,
                    dataSource: next,
                    // New widgets track the source's starter request;
                    // edited widgets keep their hand-tuned one.
                    request: widget ? d.request : defaultRequestFor(next),
                  }));
                }}
              >
                {(dataSources.data ?? []).map((ds) => (
                  <MenuItem key={ds.id} value={ds.id}>
                    {ds.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="widget-type-label">Display type</InputLabel>
              <Select
                labelId="widget-type-label"
                label="Display type"
                value={draft.type}
                onChange={(e) => set('type', e.target.value as CustomViewWidgetType)}
              >
                {WIDGET_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="widget-size-label">Width (columns)</InputLabel>
              <Select
                labelId="widget-size-label"
                label="Width (columns)"
                value={draft.size}
                onChange={(e) => set('size', Number(e.target.value))}
              >
                {SPAN_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="widget-rows-label">Height (rows)</InputLabel>
              <Select
                labelId="widget-rows-label"
                label="Height (rows)"
                value={draft.rows}
                onChange={(e) => set('rows', Number(e.target.value))}
              >
                {SPAN_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            label="What should this widget display?"
            fullWidth
            multiline
            minRows={3}
            value={draft.prompt}
            onChange={(e) => set('prompt', e.target.value)}
            helperText="Describe the widget's content in your own words — the AI agent generates the widget's analytics request from it on every save. Editing the request in the technical definition view overrides generation."
          />
          {draft.type !== 'markdown' && (
            <TextField
              label="Style & rendering (optional)"
              fullWidth
              multiline
              minRows={2}
              value={draft.stylePrompt}
              onChange={(e) => set('stylePrompt', e.target.value)}
              helperText='How the widget should look, in your own words — e.g. "green palette only", "pie on one half, results list on the other". Compiled into the display options on save.'
            />
          )}
          {draft.type === 'markdown' && (
            <TextField
              label="Markdown content"
              fullWidth
              multiline
              minRows={6}
              value={draft.markdown}
              onChange={(e) => set('markdown', e.target.value)}
              slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: 13 } } }}
            />
          )}
        </Stack>
      )}
    </FormDialog>
  );
}
